import { Badge, DataGridLeafColumn } from '@ibsys/kit';
import { ComponentProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { C2_EDDY_LOAD_STATUSES, type C2EddyLoadItem, type C2EddyLoadStatus } from '@/entities/integrations';

import { C2EddyConversationLink } from './C2EddyConversationLink';
import { C2EddyDateTime } from './C2EddyDateTime';
import { C2EddyTicketNumber } from './C2EddyTicketNumber';

/** Значения нет — ячейка остаётся пустой */
const EMPTY_CELL = '';

const STATUS_BADGE_VARIANT: Record<C2EddyLoadStatus, ComponentProps<typeof Badge>['variant']> = {
  loading: 'secondary',
  loaded: 'primary',
  error: 'destructive',
};

/**
 * Колонки журнала загрузок обращений HelpDeskEddy: обращение, результат с отбором по нему и
 * ошибкой под ним, счётчики переписки и картинок, коммуникация и время загрузки. Ширины в сумме
 * укладываются в карточку интеграции и на экране ноутбука (окно от 1280 px с развёрнутым меню):
 * ссылка на коммуникацию и время видны без прокрутки вбок. Колонки не уже самого длинного слова
 * заголовка и плашки результата; номер длиннее колонки переносится, а не обрезается; время в ru
 * помещается в строку при любом часе.
 */
export const useC2EddyLoadColumns = (): DataGridLeafColumn<C2EddyLoadItem>[] => {
  const { t } = useTranslation(['integrations']);

  return useMemo(
    () => [
      {
        id: 'uniqueId',
        header: t('integrations:c2Eddy.journal.uniqueId'),
        size: 110,
        wrap: true,
        // Номера у обращения может не быть — тогда строку опознаёт id обращения в HelpDeskEddy
        cell: ({ row }) => <C2EddyTicketNumber value={row.uniqueId ?? String(row.eddyTicketId)} />,
      },
      {
        id: 'status',
        header: t('integrations:c2Eddy.journal.status'),
        size: 140,
        wrap: true,
        enableFiltering: true,
        filter: {
          type: 'select',
          options: C2_EDDY_LOAD_STATUSES.map((status) => ({
            value: status,
            label: t(`integrations:c2Eddy.journal.statuses.${status}`),
          })),
        },
        // Ошибка поясняет результат и бывает редко: своя колонка вытолкнула бы ссылку и время за край карточки
        cell: ({ row }) => (
          <div className="flex flex-col items-start gap-1">
            <Badge variant={STATUS_BADGE_VARIANT[row.status]}>
              {t(`integrations:c2Eddy.journal.statuses.${row.status}`)}
            </Badge>
            {row.error && <span>{row.error}</span>}
          </div>
        ),
      },
      {
        id: 'messagesCount',
        header: t('integrations:c2Eddy.journal.messagesCount'),
        accessor: 'messagesCount',
        size: 100,
      },
      {
        id: 'attachmentsCount',
        header: t('integrations:c2Eddy.journal.attachmentsCount'),
        accessor: 'attachmentsCount',
        size: 105,
      },
      {
        id: 'imagesUnavailable',
        header: t('integrations:c2Eddy.journal.imagesUnavailable'),
        accessor: 'imagesUnavailable',
        size: 100,
      },
      {
        id: 'conversationId',
        header: t('integrations:c2Eddy.journal.conversationId'),
        size: 120,
        cell: ({ row }) =>
          row.conversationId === null ? EMPTY_CELL : <C2EddyConversationLink conversationId={row.conversationId} />,
      },
      {
        id: 'updatedAt',
        header: t('integrations:c2Eddy.journal.updatedAt'),
        size: 160,
        wrap: true,
        cell: ({ row }) => (row.updatedAt ? <C2EddyDateTime value={row.updatedAt} /> : EMPTY_CELL),
      },
    ],
    [t],
  );
};
